/**
 * Invoicing & Billing admin API module.
 *
 * Extracted from the AdminService god class in services/auth.ts and converted
 * to the central web API client. Covers: invoice configuration, invoice
 * schedules, invoice delivery lists, invoice batches, billing (system-admin
 * billing config/report/invoice PDF + email), and usage billing invoices.
 *
 * Method names, signatures, and observable behavior (error messages, query
 * building, dedup keys) mirror the originals exactly so the composed
 * adminService export stays drop-in compatible.
 */

import { webApiClient, unwrap } from '../apiClient';
import type { ApiResponse } from '@turn-connect/shared/web';
import { dedup } from './helpers';
import type {
  InvoiceBatchConfigWithRules,
  UpdateInvoiceBatchConfigRequest,
  InvoiceBatchConfig,
  InvoiceSchedule,
  CreateInvoiceScheduleRequest,
  UpdateInvoiceScheduleRequest,
  InvoiceDeliveryList,
  InvoiceCoverageResponse,
  CreateInvoiceDeliveryListRequest,
  UpdateInvoiceDeliveryListRequest,
  ListInvoiceBatchesResponse,
  InvoiceBatch,
  Activity,
  BillingReportData,
  AccountBillingConfig,
  UsageBillingInvoiceListResponse,
} from '@turn-connect/shared';
import type { ApiUsageBillingInvoice } from '../../types/api';

export async function getInvoiceConfig(accountId: string): Promise<InvoiceBatchConfigWithRules> {
  return dedup(`getInvoiceConfig:${accountId}`, async () => {
    const res = await webApiClient.get<InvoiceBatchConfigWithRules>(`/accounts/${accountId}/invoice-config`);
    if (!res.success) {
      throw new Error(res.error || 'Failed to fetch invoice config');
    }
    return res.data as InvoiceBatchConfigWithRules;
  });
}

export async function updateInvoiceConfig(accountId: string, data: UpdateInvoiceBatchConfigRequest): Promise<InvoiceBatchConfig> {
  return unwrap(webApiClient.put<InvoiceBatchConfig>(`/accounts/${accountId}/invoice-config`, data), 'Failed to update invoice config');
}

// ───────────────────────────────────────────────────────────────────────
// Invoice batch rules surface (invoice_schedules + invoice_delivery_lists).
// Mounted at /api/accounts/:id/invoice-schedules + /api/invoice-schedules/:id
// and the symmetric /invoice-delivery-lists routes. See Phase 1.4 in
// turn-connect/internal/api/handlers/invoice_schedule_handler.go.
// ───────────────────────────────────────────────────────────────────────

// (spec: GET /accounts/{account_id}/invoice-schedules ->
// handlers.ListInvoiceSchedulesResponse and /invoice-delivery-lists ->
// handlers.ListInvoiceDeliveryListsResponse. The spec documents both modes:
// the { items, total, limit, offset } envelope when ?limit/?offset is
// supplied, and the bare-array back-compat mode when neither is present —
// which is what the unpaginated helpers below consume. /invoice-batches ->
// handlers.ListInvoiceBatchesResponse ({ batches, total, limit, offset },
// always the envelope; no bare-array mode). Single-item GET/POST/PUT are
// entities.InvoiceSchedule / entities.InvoiceDeliveryList /
// entities.InvoiceBatch as before.)
export async function listInvoiceSchedules(accountId: string): Promise<InvoiceSchedule[]> {
  const res = await webApiClient.get<InvoiceSchedule[]>(`/accounts/${accountId}/invoice-schedules`);
  if (!res.success) throw new Error('Failed to fetch invoice schedules');
  return res.data as InvoiceSchedule[];
}

/**
 * Search + narrowing sent to the rules listings.
 *
 * Every field is applied SERVER-side. A client-side post-filter on a paginated
 * page returns short pages (3 of 10) while "Page N of M" is still derived from
 * the unfiltered total.
 *
 * `locationIds` / `tagIds` match rules that NAME those locations/tags — a
 * catch-all that would merely apply is deliberately not returned. That is the
 * contract for "find the rule I need to edit"; "what would apply here?" is
 * answered by the coverage view instead. Chips AND together.
 *
 * `recipientEmail` matches a substring anywhere in a recipient address, because
 * 243 of 244 production recipients have no name recorded. Schedules have no
 * recipients, so a schedule search carrying one returns nothing — which is what
 * keeps the cross-tab "N more in Delivery lists" count honest.
 */
export interface InvoiceRuleQuery {
  enabled?: boolean;
  name?: string;
  locationIds?: string[];
  tagIds?: string[];
  recipientEmail?: string;
  /**
   * How a location chip matches: 'applies' (the server's default — every rule
   * that reaches the location, each row saying how) or 'named' (only the
   * rules that name it).
   */
  locationMatch?: 'applies' | 'named';
}

/** Serialises an InvoiceRuleQuery onto a paginated request. */
function applyRuleQuery(params: URLSearchParams, query?: InvoiceRuleQuery): URLSearchParams {
  if (!query) return params;
  if (query.enabled !== undefined) params.set('enabled', String(query.enabled));
  if (query.name) params.set('name', query.name);
  if (query.locationIds && query.locationIds.length > 0) params.set('location_ids', query.locationIds.join(','));
  if (query.tagIds && query.tagIds.length > 0) params.set('tag_ids', query.tagIds.join(','));
  if (query.recipientEmail) params.set('recipient_email', query.recipientEmail);
  if (query.locationMatch === 'named' && query.locationIds?.length) params.set('location_match', 'named');
  return params;
}

/**
 * Paginated variant for the management UI. Backend returns
 * { items, total, limit, offset } when ?limit or ?offset is supplied.
 * The non-paginated listInvoiceSchedules above stays so callers that
 * want every row (rare) don't have to change.
 */
export async function listInvoiceSchedulesPaginated(
  accountId: string,
  limit = 20,
  offset = 0,
  query?: InvoiceRuleQuery
): Promise<{ items: InvoiceSchedule[]; total: number; limit: number; offset: number }> {
  const params = applyRuleQuery(new URLSearchParams({ limit: String(limit), offset: String(offset) }), query);
  const res = await webApiClient.get<{ items: InvoiceSchedule[]; total: number; limit: number; offset: number }>(
    `/accounts/${accountId}/invoice-schedules?${params.toString()}`
  );
  if (!res.success) throw new Error('Failed to fetch invoice schedules');
  return res.data as { items: InvoiceSchedule[]; total: number; limit: number; offset: number };
}

/**
 * Like `unwrap`, but keeps the parsed error body on the thrown error as
 * `errorBody`. The shared ApiClient flattens a non-2xx body to its prose
 * `error`, so a structured `code` (e.g. INVOICE_SCHEDULE_UNKNOWN_TAG_NAME)
 * only survives there. See describeInvoiceRuleSaveError.
 */
async function unwrapKeepingBody<T>(pending: Promise<ApiResponse<T>>, fallbackMessage: string): Promise<T> {
  const res = await pending;
  if (!res.success) {
    const err = new Error(res.error || fallbackMessage) as Error & { errorBody?: unknown };
    if (res.errorBody) err.errorBody = res.errorBody;
    throw err;
  }
  return res.data as T;
}

export async function createInvoiceSchedule(accountId: string, data: CreateInvoiceScheduleRequest): Promise<InvoiceSchedule> {
  return unwrapKeepingBody(
    webApiClient.post<InvoiceSchedule>(`/accounts/${accountId}/invoice-schedules`, data),
    'Failed to create invoice schedule'
  );
}

export async function getInvoiceSchedule(scheduleId: string): Promise<InvoiceSchedule> {
  const res = await webApiClient.get<InvoiceSchedule>(`/invoice-schedules/${encodeURIComponent(scheduleId)}`);
  if (!res.success) throw new Error('Failed to fetch invoice schedule');
  return res.data as InvoiceSchedule;
}

export async function updateInvoiceSchedule(scheduleId: string, data: UpdateInvoiceScheduleRequest): Promise<InvoiceSchedule> {
  return unwrapKeepingBody(
    webApiClient.put<InvoiceSchedule>(`/invoice-schedules/${scheduleId}`, data),
    'Failed to update invoice schedule'
  );
}

export async function deleteInvoiceSchedule(scheduleId: string): Promise<void> {
  await unwrap(webApiClient.delete<void>(`/invoice-schedules/${scheduleId}`), 'Failed to delete invoice schedule');
}

export async function listInvoiceDeliveryLists(accountId: string): Promise<InvoiceDeliveryList[]> {
  const res = await webApiClient.get<InvoiceDeliveryList[]>(`/accounts/${accountId}/invoice-delivery-lists`);
  if (!res.success) throw new Error('Failed to fetch invoice delivery lists');
  return res.data as InvoiceDeliveryList[];
}

/** Paginated variant — see listInvoiceSchedulesPaginated docs. */
export async function listInvoiceDeliveryListsPaginated(
  accountId: string,
  limit = 20,
  offset = 0,
  query?: InvoiceRuleQuery
): Promise<{ items: InvoiceDeliveryList[]; total: number; limit: number; offset: number }> {
  const params = applyRuleQuery(new URLSearchParams({ limit: String(limit), offset: String(offset) }), query);
  const res = await webApiClient.get<{ items: InvoiceDeliveryList[]; total: number; limit: number; offset: number }>(
    `/accounts/${accountId}/invoice-delivery-lists?${params.toString()}`
  );
  if (!res.success) throw new Error('Failed to fetch invoice delivery lists');
  return res.data as { items: InvoiceDeliveryList[]; total: number; limit: number; offset: number };
}

/**
 * The invoice batches one rule is responsible for, newest first, for the
 * lazily-loaded second tab on each rule editor. Both endpoints return the
 * same { batches, total, limit, offset } envelope so a single component
 * renders either side.
 *
 * `listScheduleBatches` answers "what did this rule CREATE" and
 * `listDeliveryListBatches` "what did this rule SEND" — the two ends of the
 * invoicing pipeline, not two views of the same set.
 */
export async function listScheduleBatches(scheduleId: string, limit = 20, offset = 0): Promise<ListInvoiceBatchesResponse> {
  const res = await webApiClient.get<ListInvoiceBatchesResponse>(
    `/invoice-schedules/${scheduleId}/batches?limit=${limit}&offset=${offset}`
  );
  if (!res.success) throw new Error('Failed to fetch invoices created by this rule');
  return res.data as ListInvoiceBatchesResponse;
}

/** See listScheduleBatches. */
export async function listDeliveryListBatches(listId: string, limit = 20, offset = 0): Promise<ListInvoiceBatchesResponse> {
  const res = await webApiClient.get<ListInvoiceBatchesResponse>(
    `/invoice-delivery-lists/${listId}/batches?limit=${limit}&offset=${offset}`
  );
  if (!res.success) throw new Error('Failed to fetch invoices sent by this list');
  return res.data as ListInvoiceBatchesResponse;
}

/**
 * One rule's change history — who created, changed or deleted it, where they
 * were working, and what each setting was before and after. Backs the rule
 * editors' lazily-loaded History tab. Newest first; same authorisation as
 * reading the rule.
 */
export interface RuleHistoryResponse {
  activities: Activity[];
  total: number;
  limit: number;
  offset: number;
}

function normaliseHistory(data: RuleHistoryResponse | undefined, limit: number, offset: number): RuleHistoryResponse {
  return {
    activities: Array.isArray(data?.activities) ? data!.activities : [],
    total: data?.total ?? 0,
    limit: data?.limit ?? limit,
    offset: data?.offset ?? offset,
  };
}

export async function listInvoiceScheduleHistory(scheduleId: string, limit = 20, offset = 0): Promise<RuleHistoryResponse> {
  const res = await webApiClient.get<RuleHistoryResponse>(`/invoice-schedules/${scheduleId}/history?limit=${limit}&offset=${offset}`);
  if (!res.success) throw new Error('Failed to load the history of this schedule');
  return normaliseHistory(res.data, limit, offset);
}

/** See listInvoiceScheduleHistory. */
export async function listDeliveryListHistory(listId: string, limit = 20, offset = 0): Promise<RuleHistoryResponse> {
  const res = await webApiClient.get<RuleHistoryResponse>(`/invoice-delivery-lists/${listId}/history?limit=${limit}&offset=${offset}`);
  if (!res.success) throw new Error('Failed to load the history of this delivery list');
  return normaliseHistory(res.data, limit, offset);
}

export async function createInvoiceDeliveryList(accountId: string, data: CreateInvoiceDeliveryListRequest): Promise<InvoiceDeliveryList> {
  return unwrapKeepingBody(
    webApiClient.post<InvoiceDeliveryList>(`/accounts/${accountId}/invoice-delivery-lists`, data),
    'Failed to create invoice delivery list'
  );
}

export async function getInvoiceDeliveryList(listId: string): Promise<InvoiceDeliveryList> {
  const res = await webApiClient.get<InvoiceDeliveryList>(`/invoice-delivery-lists/${encodeURIComponent(listId)}`);
  if (!res.success) throw new Error('Failed to fetch invoice delivery list');
  return res.data as InvoiceDeliveryList;
}

export async function updateInvoiceDeliveryList(listId: string, data: UpdateInvoiceDeliveryListRequest): Promise<InvoiceDeliveryList> {
  return unwrapKeepingBody(
    webApiClient.put<InvoiceDeliveryList>(`/invoice-delivery-lists/${listId}`, data),
    'Failed to update invoice delivery list'
  );
}

export async function deleteInvoiceDeliveryList(listId: string): Promise<void> {
  await unwrap(webApiClient.delete<void>(`/invoice-delivery-lists/${listId}`), 'Failed to delete invoice delivery list');
}

/** Outcome of a Send now. See {@link sendInvoiceDeliveryListNow}. */
export interface SendInvoiceDeliveryListNowResult {
  /**
   * Nullable on purpose: the field is contractually an array, but an API build
   * that returns a nil slice sends `null`. Typing it honestly forces callers to
   * handle that instead of reading `.length` off it and crashing.
   */
  delivery_ids: string[] | null;
  batch_count: number;
  /**
   * Set when nothing was sent, or when some invoices wait for someone to hold
   * the list's contact role. This is a success, not a failure, so callers
   * should show it as information rather than an error.
   */
  reason?: string;
  /** Invoices left waiting because nobody holds the list's role at their store. */
  waiting?: number;
}

/**
 * Send a delivery list's outstanding batches immediately, bypassing its
 * cadence.
 *
 * Only batches the list has not already delivered go out, so pressing this
 * twice is a no-op the second time rather than a duplicate email to the
 * customer. A list that is disabled, has no recipients, or matches nothing is
 * rejected with 400 and an explanatory message.
 */
export async function sendInvoiceDeliveryListNow(listId: string): Promise<SendInvoiceDeliveryListNowResult> {
  return unwrap(
    webApiClient.post<SendInvoiceDeliveryListNowResult>(`/invoice-delivery-lists/${listId}/send-now`, {}),
    'Failed to send invoice delivery list'
  );
}

/** One tag NAME in GET /accounts/:account_id/tags/summary. */
export interface TagSummaryItem {
  /** Display name (the most common casing). Matching is case-insensitive. */
  name: string;
  /** Number of in-scope locations that have a live tag with this name. */
  location_count: number;
  locations: { id: string; name: string }[];
  /** The underlying per-location tag ids (for id-based surfaces: schedules, batch filters). */
  tag_ids: string[];
}

/**
 * The account's tag names, grouped case-insensitively, in ONE request —
 * scoped to `locationIds` when given, else every live location.
 *
 * Replaces the per-location listLocationTags fan-out the invoicing surfaces
 * used to do. That fan-out fired one request per location at once; on a
 * 128-location account the ingress connection limit 503'd about half of them,
 * each failure was swallowed as [] and the delivery-rule editor then saved
 * rules with their tags wiped. This THROWS on failure — callers must surface
 * it, never treat it as an empty catalog.
 */
export async function listAccountTagSummary(accountId: string, locationIds?: string[]): Promise<TagSummaryItem[]> {
  const ids = [...(locationIds ?? [])].sort();
  const qs = ids.length > 0 ? `?location_ids=${ids.map(encodeURIComponent).join(',')}` : '';
  const res = await unwrap(
    webApiClient.get<{ items: TagSummaryItem[] | null }>(`/accounts/${accountId}/tags/summary${qs}`),
    'Failed to load tags'
  );
  // The server always sends an array; a null from an older build is still a
  // successful (empty) answer, not a failure.
  return (Array.isArray(res?.items) ? res.items : []).map((i) => ({
    ...i,
    locations: i.locations ?? [],
    tag_ids: i.tag_ids ?? [],
  }));
}

/**
 * Get the "untargeted locations" coverage report for an account.
 * Drives the Invoicing tab banners that flag locations not covered
 * by any enabled schedule (creation) or delivery list (distribution).
 */
export async function getInvoiceCoverage(accountId: string): Promise<InvoiceCoverageResponse> {
  return unwrap(webApiClient.get<InvoiceCoverageResponse>(`/accounts/${accountId}/invoice-coverage`), 'Failed to load invoice coverage');
}

// Invoice Batches
export async function listInvoiceBatches(
  accountId: string,
  limit = 20,
  offset = 0,
  search?: string,
  locationId?: string,
  tagIds?: string[],
  paymentStatuses?: string[],
  sort?: { column: string; dir: 'ASC' | 'DESC' }
): Promise<ListInvoiceBatchesResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set('search', search);
  if (locationId) params.set('location_id', locationId);
  // Tag filter is a comma-separated UUID list — matches the location_ids
  // convention on the backend handler. Folded tag IDs are expanded to
  // every underlying per-location UUID before they hit this call.
  if (tagIds && tagIds.length > 0) params.set('tag_ids', tagIds.join(','));
  // Payment status filter — comma-separated list of: paid_in_full,
  // partial, unpaid, voided, none. The special "none" value matches
  // batches with no accounting_sync_mappings row OR a NULL
  // payment_status (never polled / no payment event yet).
  if (paymentStatuses && paymentStatuses.length > 0) params.set('payment_status', paymentStatuses.join(','));
  // Server-side sort. column is validated against the backend's
  // whitelist (see sortableBatchColumns in the postgres repo) —
  // unknown columns fall back to created_at DESC; dir must be ASC
  // or DESC (handler 400s otherwise).
  if (sort) {
    params.set('sort', sort.column);
    params.set('dir', sort.dir);
  }
  const res = await webApiClient.get<ListInvoiceBatchesResponse>(`/accounts/${accountId}/invoice-batches?${params.toString()}`);
  if (!res.success) {
    throw new Error('Failed to fetch invoices');
  }
  return res.data as ListInvoiceBatchesResponse;
}

// Global top-bar search backed by GET /invoice-batches/search.
// The backend pins the account scope to the caller's JWT (system admins may
// pass account_id but the global header doesn't need to — the JWT is enough).
// (spec: GET /invoice-batches/search -> handlers.ListInvoiceBatchesResponse,
// the { batches, total, limit, offset } envelope; the shared
// ListInvoiceBatchesResponse mirrors it with `batches` required.)
export async function searchInvoiceBatches(query: string, limit = 8): Promise<InvoiceBatch[]> {
  const q = (query || '').trim();
  if (!q) return [];
  const params = new URLSearchParams({ query: q, limit: String(limit) });
  const res = await webApiClient.get<ListInvoiceBatchesResponse>(`/invoice-batches/search?${params.toString()}`);
  if (!res.success) {
    // Soft-fail: the global header runs this alongside work-order search;
    // a transient batch-search error shouldn't blank out WO results.
    console.warn('searchInvoiceBatches failed', res.status);
    return [];
  }
  const json = res.data;
  return Array.isArray(json?.batches) ? json.batches : [];
}

export async function getInvoiceBatch(batchId: string): Promise<InvoiceBatch> {
  const res = await webApiClient.get<InvoiceBatch>(`/invoice-batches/${batchId}`);
  if (!res.success) {
    throw new Error('Failed to fetch invoice');
  }
  return res.data as InvoiceBatch;
}

export async function resendInvoiceBatch(batchId: string, overrideEmail?: string): Promise<void> {
  const body: { override_email?: string } = {};
  if (overrideEmail) body.override_email = overrideEmail;
  await unwrap(webApiClient.post<void>(`/invoice-batches/${batchId}/resend`, body), 'Failed to resend invoice');
}

/**
 * Resend a specific delivery (creator, distributor, or prior resend).
 * Backend: POST /api/invoice-deliveries/{delivery_id}/resend
 */
export async function resendDelivery(deliveryId: string): Promise<void> {
  await unwrap(webApiClient.post<void>(`/invoice-deliveries/${deliveryId}/resend`, {}), 'Failed to resend delivery');
}

export async function createManualBatch(accountId: string, data: Record<string, unknown>): Promise<InvoiceBatch> {
  return unwrap(webApiClient.post<InvoiceBatch>(`/accounts/${accountId}/invoice-batches`, data), 'Failed to create invoice');
}

// ==========================================
// Billing (System Admin Only)
// ==========================================

// (spec: GET/PUT /admin/accounts/{account_id}/billing ->
// handlers.BillingConfigResponse. The shared AccountBillingConfig mirrors it
// field-for-field; we use the shared type because the generated one is
// all-optional — swag emits no `required` markers — and AccountBillingTab
// relies on the rate streams/flags shape.)
export async function getBillingConfig(accountId: string): Promise<AccountBillingConfig> {
  return dedup(`getBillingConfig:${accountId}`, async () => {
    const res = await webApiClient.get<AccountBillingConfig>(`/admin/accounts/${accountId}/billing`);
    if (!res.success) {
      throw new Error('Failed to fetch billing config');
    }
    return res.data as AccountBillingConfig;
  });
}

// updateBillingConfig replaces the rate streams (platform + per-user) and
// optional config flags in a single atomic PUT. Either stream may be empty
// (an account can be platform-only, per-user-only, or both).
// (spec: request body is handlers.UpdateBillingConfigRequest, which the
// inline body type below mirrors; response is handlers.BillingConfigResponse.)
export async function updateBillingConfig(
  accountId: string,
  body: {
    platform_rates: Array<{
      unit_amount: string;
      effective_from: string;
      effective_to?: string | null;
      notes?: string | null;
    }>;
    per_user_rates: Array<{
      unit_amount: string;
      effective_from: string;
      effective_to?: string | null;
      notes?: string | null;
    }>;
    auto_generate_enabled?: boolean;
    skip_accounting_sync?: boolean;
    billing_contact_email?: string;
    billing_contact_name?: string;
  }
): Promise<AccountBillingConfig> {
  return unwrap(webApiClient.put<AccountBillingConfig>(`/admin/accounts/${accountId}/billing`, body), 'Failed to update billing config');
}

// (spec: GET /admin/billing/report -> entities.BillingReportData. The shared
// BillingReportData interface mirrors it field-for-field; we use the shared
// type because the generated one is all-optional — swag emits no `required`
// markers — and consumers rely on lines/months/grand_total being present.)
export async function getBillingReportData(
  beginDate: string,
  endDate: string,
  options?: { includeUsers?: boolean; includeDetailedUsers?: boolean; accountIds?: string[] }
): Promise<BillingReportData> {
  let url = `/admin/billing/report?begin_date=${beginDate}&end_date=${endDate}`;

  if (options?.includeUsers) {
    url += '&include_users=true';
  }

  if (options?.includeDetailedUsers) {
    url += '&include_detailed_users=true';
  }

  if (options?.accountIds && options.accountIds.length > 0) {
    url += `&account_ids=${encodeURIComponent(options.accountIds.join(','))}`;
  }

  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error('Failed to fetch billing report data');
  }
  return res.data;
}

export async function downloadBillingInvoicePdf(
  accountId: string,
  beginDate: string,
  endDate: string,
  includeUsers: boolean
): Promise<Blob> {
  const res = await webApiClient.post<Blob>(`/admin/billing/invoice/pdf`, {
    account_id: accountId,
    begin_date: beginDate,
    end_date: endDate,
    include_users: includeUsers,
  });
  if (!res.success) {
    // Legacy behavior: surface the JSON body's `error` field only, falling
    // back to the generic message (j.message was never consulted here).
    throw new Error(res.errorBody?.error || 'Failed to generate PDF');
  }
  return res.data as Blob;
}

export async function emailBillingInvoice(
  accountId: string,
  beginDate: string,
  endDate: string,
  includeUsers: boolean,
  recipientEmail: string
): Promise<{ message: string; recipient: string; invoice_number: string }> {
  const res = await webApiClient.post<{ message: string; recipient: string; invoice_number: string }>(`/admin/billing/invoice/email`, {
    account_id: accountId,
    begin_date: beginDate,
    end_date: endDate,
    include_users: includeUsers,
    recipient_email: recipientEmail,
  });
  if (!res.success) {
    // Legacy behavior: `error` field only — see downloadBillingInvoicePdf.
    throw new Error(res.errorBody?.error || 'Failed to email billing invoice');
  }
  return res.data as { message: string; recipient: string; invoice_number: string };
}

// Usage Billing Invoices

// (spec: GET /accounts/{account_id}/usage-invoices and GET
// /admin/usage-invoices -> handlers.ListUsageBillingInvoicesResponse, the
// standard list envelope { items: entities.UsageBillingInvoice[], total,
// limit, offset }. The shared UsageBillingInvoiceListResponse mirrors it;
// used because the generated type is all-optional and callers rely on
// items/total being present.)
export async function listUsageBillingInvoices(accountId: string, limit = 20, offset = 0): Promise<UsageBillingInvoiceListResponse> {
  const res = await webApiClient.get<UsageBillingInvoiceListResponse>(
    `/accounts/${accountId}/usage-invoices?limit=${limit}&offset=${offset}`
  );
  if (!res.success) throw new Error('Failed to list usage billing invoices');
  return res.data as UsageBillingInvoiceListResponse;
}

export async function getUsageBillingInvoice(invoiceId: string): Promise<ApiUsageBillingInvoice> {
  // spec: GET /usage-invoices/{invoice_id} -> entities.UsageBillingInvoice
  const res = await webApiClient.get<ApiUsageBillingInvoice>(`/usage-invoices/${invoiceId}`);
  if (!res.success) throw new Error('Failed to get usage billing invoice');
  return res.data as ApiUsageBillingInvoice;
}

export async function generateUsageBillingInvoice(accountId: string, year: number, month: number): Promise<ApiUsageBillingInvoice> {
  // spec: POST /accounts/{account_id}/usage-invoices/generate -> 201 entities.UsageBillingInvoice
  const res = await webApiClient.post<ApiUsageBillingInvoice>(`/accounts/${accountId}/usage-invoices/generate`, { year, month });
  if (!res.success) throw new Error('Failed to generate usage billing invoice');
  return res.data as ApiUsageBillingInvoice;
}

export async function resendUsageBillingInvoice(invoiceId: string, email?: string): Promise<void> {
  const res = await webApiClient.post<void>(`/usage-invoices/${invoiceId}/resend`, email ? { email } : {});
  if (!res.success) throw new Error('Failed to resend usage billing invoice');
}

export async function listAllUsageBillingInvoices(
  limit = 20,
  offset = 0,
  opts?: {
    status?: string;
    sync_status?: 'not_synced' | 'pending' | 'synced' | 'failed' | string;
    paid?: boolean;
    q?: string;
  }
): Promise<UsageBillingInvoiceListResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (opts?.status) params.set('status', opts.status);
  if (opts?.sync_status) params.set('sync_status', opts.sync_status);
  if (opts?.paid !== undefined) params.set('paid', opts.paid ? 'true' : 'false');
  if (opts?.q) params.set('q', opts.q);

  const res = await webApiClient.get<UsageBillingInvoiceListResponse>(`/admin/usage-invoices?${params.toString()}`);
  if (!res.success) throw new Error('Failed to list usage billing invoices');
  return res.data as UsageBillingInvoiceListResponse;
}

export async function retrySyncUsageBillingInvoice(invoiceId: string): Promise<void> {
  // Legacy POST sent no body.
  const res = await webApiClient.post<{ state?: string; error_code?: string; error_message?: string }>(
    `/admin/usage-invoices/${invoiceId}/sync`,
    undefined
  );
  if (!res.success) {
    // Legacy behavior: JSON body's `error` field only, else the fixed message.
    throw new Error(res.errorBody?.error || 'Retry sync failed');
  }

  // The endpoint answers 200 even when the provider call failed or was
  // declined — the outcome rides in the body. Without this a failed retry is
  // indistinguishable from a working one, which is the exact failure shape
  // this button exists to correct.
  const state = res.data?.state;
  if (state === 'skipped') {
    // Almost always the demo flag still being set: SyncInvoice short-circuits
    // on skip_accounting_sync before it ever calls the provider, so pressing
    // Retry again will keep returning here until the flag is cleared. Say
    // which knob to turn rather than reporting a bare "skipped".
    throw new Error('Sync skipped — this account is still set to skip QuickBooks sync. Clear that setting above, save, then retry.');
  }
  if (state && state !== 'synced') {
    const code = res.data?.error_code ? `[${res.data.error_code}] ` : '';
    throw new Error(`${code}${res.data?.error_message || `sync returned ${state}`}`);
  }
}

// markUsageBillingInvoicePaid removed: QuickBooks is the system of record.
// is_paid only flips via the QBO Payment webhook handled by
// UsageBillingPaymentReconciler on the backend.

export async function markUsageBillingInvoiceUnpaid(invoiceId: string): Promise<void> {
  // Legacy POST sent no body.
  const res = await webApiClient.post<void>(`/usage-invoices/${invoiceId}/unpaid`, undefined);
  if (!res.success) throw new Error('Failed to mark invoice as unpaid');
}

export async function viewUsageBillingPDF(invoiceId: string): Promise<string> {
  // The client auto-detects application/pdf and returns a Blob.
  const res = await webApiClient.get<Blob>(`/usage-invoices/${invoiceId}/pdf`);
  if (!res.success) throw new Error('Failed to fetch PDF');
  const arrayBuffer = await (res.data as Blob).arrayBuffer();
  // Convert to base64 data URI for reliable iframe embedding
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return `data:application/pdf;base64,${base64}`;
}

export const invoicingBillingAdmin = {
  getInvoiceConfig,
  updateInvoiceConfig,
  listInvoiceSchedules,
  listInvoiceSchedulesPaginated,
  createInvoiceSchedule,
  getInvoiceSchedule,
  updateInvoiceSchedule,
  deleteInvoiceSchedule,
  listInvoiceDeliveryLists,
  listInvoiceDeliveryListsPaginated,
  listScheduleBatches,
  listDeliveryListBatches,
  listInvoiceScheduleHistory,
  listDeliveryListHistory,
  createInvoiceDeliveryList,
  getInvoiceDeliveryList,
  updateInvoiceDeliveryList,
  deleteInvoiceDeliveryList,
  sendInvoiceDeliveryListNow,
  getInvoiceCoverage,
  listAccountTagSummary,
  listInvoiceBatches,
  searchInvoiceBatches,
  getInvoiceBatch,
  resendInvoiceBatch,
  resendDelivery,
  createManualBatch,
  getBillingConfig,
  updateBillingConfig,
  getBillingReportData,
  downloadBillingInvoicePdf,
  emailBillingInvoice,
  listUsageBillingInvoices,
  getUsageBillingInvoice,
  generateUsageBillingInvoice,
  resendUsageBillingInvoice,
  listAllUsageBillingInvoices,
  retrySyncUsageBillingInvoice,
  markUsageBillingInvoiceUnpaid,
  viewUsageBillingPDF,
};
